import { useAgeCopy } from "@/lib/ageCopy";
import React from "react";
import { Link } from "react-router-dom";
import { track } from "@/lib/track";
import { SUBJECTS } from "@/lib/canonical";
import { getLearningProfile } from "@/lib/learningProfile";
import { loadFocus } from "@/lib/focusDay";
import { COUNTRIES } from "@/data/countries";
import LevelPicker from "@/components/LevelPicker";
import { useLevel } from "@/lib/learnerLevel";

/** WHAT DO YOU FEEL LIKE? — four primary choices into EXISTING experiences (no duplicate routes). */
export function HomeChoices() {
  const C = [
    { id: "learn", e: "📚", l: "Learn", s: "Lessons", to: "/learn-pathways", bg: "bg-emerald-100" },
    { id: "play", e: "🎮", l: "Play", s: "Games", to: "/play", bg: "bg-fuchsia-100" },
    { id: "explore", e: "🌍", l: "Explore", s: "Globe", to: "/globe", bg: "bg-sky-100" },
    { id: "create", e: "🎨", l: "Create", s: "Colour & draw", to: "/coloring", bg: "bg-amber-100" },
  ];
  return (
    <section className="mt-5" data-testid="home-choices" aria-labelledby="home-choices-title">
      <h2 id="home-choices-title" className="mwq-card-title">Your worlds</h2>
      <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-3">
        {C.map((c) => (
          <Link key={c.id} to={c.to} data-testid={`home-choice-${c.id}`} onClick={() => track("home_choice_clicked", { id: c.id })} className={`min-h-[56px] rounded-2xl border-[3px] border-slate-900 ${c.bg} px-3 py-2 flex items-center gap-2 shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:-translate-y-0.5 transition-transform min-w-0`}>
            <span aria-hidden className="text-2xl leading-none shrink-0">{c.e}</span>
            <span className="min-w-0"><span className="block font-heading font-black text-base leading-none">{c.l}</span><span className="block text-[11px] font-bold text-slate-600 mt-0.5 truncate">{c.s}</span></span>
          </Link>
        ))}
      </div>
    </section>
  );
}

const CORE = ["maths", "english", "science", "history", "geography"];
/** FOCUS TODAY — "What would you like to get really good at?" → existing Focus Learning (/focus). */
export function HomeFocusToday() {
  const level = useLevel();
  const year = level?.year;
  const focus = loadFocus();
  const subj = (id) => SUBJECTS.find((s) => s.id === id);
  return (
    <section className="mt-6 md:mt-8 rounded-3xl border-[3px] border-slate-900 bg-white p-4 shadow-[0_4px_0_0_rgba(15,23,42,1)]" data-testid="home-focus" aria-labelledby="home-focus-title">
      <p className="text-[11px] font-black uppercase tracking-widest text-slate-600">🎯 Get really good at…</p>
      <h2 id="home-focus-title" className="font-heading text-lg md:text-xl font-bold">Pick a subject to practise</h2>
      <div className="mt-2"><LevelPicker level={level} testId="home-level" /></div>
      {focus && <Link to="/focus" data-testid="home-focus-continue" className="mt-2 flex items-center justify-between min-h-[52px] px-4 rounded-2xl border-[3px] border-slate-900 bg-yellow-200 font-bold"><span>Continue practising {subj(focus.subject)?.label || focus.subject}</span><span aria-hidden>→</span></Link>}
      <div className="mt-2 flex flex-wrap gap-1.5">
        {CORE.map((id) => { const s = subj(id); return s && <Link key={id} to={year ? `/start/${id}?y=${year}` : `/focus?s=${id}`} data-testid={`home-focus-${id}`} onClick={() => track("home_focus_subject", { id })} className="min-h-[44px] inline-flex items-center gap-1 px-3 rounded-full border-2 border-slate-900 bg-white text-sm font-bold hover:bg-yellow-100"><span aria-hidden>{s.emoji}</span> Practise {s.label.replace(" & Reading", "")}</Link>; })}
        <Link to="/focus" data-testid="home-focus-more" className="min-h-[44px] inline-flex items-center px-3 rounded-full border-2 border-dashed border-slate-900 bg-white text-sm font-bold">More subjects…</Link>
      </div>
      <div className="mt-2 flex flex-wrap gap-2 text-sm">
        <Link to={year ? `/focus?y=${year}` : "/focus"} data-testid="home-focus-year" className="min-h-[40px] inline-flex items-center px-3 rounded-full border-2 border-slate-900 bg-emerald-100 font-bold">{year ? `Everything for my year (Y${year})` : "Everything for my year"}</Link>
      </div>
    </section>
  );
}

/** TODAY'S WORLD — Discover · Learn · Play · Collect for one country (existing Globe/Country/Passport routes). */
export function HomeWorld({ world, children }) {
  const copy = useAgeCopy();
  const c = world?.country; const slug = world?.slug;
  const code = c?.code ? String(c.code).toLowerCase() : "";
  // Contextual follow-ups for today's country only (Discover is the card's single primary action).
  const acts = slug && c ? [
    { id: "learn", l: `Learn about ${c.name}`, e: "📚", to: `/place/${code || slug}` },
    { id: "play", l: `${c.name} quiz`, e: "🎮", to: `/quiz?theme=${encodeURIComponent(c.name)}` },
  ] : [];
  return (
    <section className="mt-7 md:mt-9" data-testid="home-world" aria-labelledby="home-world-title">
      {/* Globe first — the world is MWQ's identity; children tap it straight away. */}
      <div className="mwq-card mwq-card-tint-success p-3 sm:p-4" data-testid="home-world-card">
        <div className="flex items-center justify-between gap-2">
          <div className="min-w-0">
            <p className="mwq-meta text-success-safe">Explore</p>
            <h2 id="home-world-title" className="mwq-card-title">{copy("exploreWorld")}</h2>
          </div>
          <Link to="/globe" data-testid="home-world-spin" onClick={() => track("globe_started", { from: "home" })} className="mwq-btn mwq-btn-secondary shrink-0 min-h-[44px] text-sm">Spin the globe</Link>
        </div>
        <div className="mt-2 min-w-0 w-full overflow-hidden rounded-2xl" data-testid="today-globe">{children}</div>
        <p className="mt-1.5 mwq-meta text-center">Tap a country to explore it</p>
        {c && (
          <div className="mt-3 rounded-2xl border border-[var(--color-border)] bg-white p-3 flex items-center justify-between gap-2" data-testid="home-world-today">
            <div className="min-w-0">
              <p className="mwq-meta">Today's world</p>
              <p className="font-extrabold text-[var(--color-text)] leading-tight truncate">{c.emoji || ""} {c.name}</p>
              <p className="mwq-meta truncate">{world.reason}</p>
            </div>
            <Link to={`/country/${slug}`} data-testid="home-world-explore" onClick={() => track("country_opened", { from: "home_today_world" })} className="mwq-btn mwq-btn-tertiary shrink-0 text-sm">Discover</Link>
          </div>
        )}
        {acts.length > 0 && <div className="mt-2 flex flex-wrap gap-2" data-testid="home-world-actions">{acts.map((a) => <Link key={a.l} to={a.to} data-testid={`home-world-${a.id}`} className="inline-flex min-h-[40px] items-center gap-1 px-3 rounded-full border border-[var(--color-border-strong)] bg-white text-sm font-bold">{a.e} {a.l}</Link>)}</div>}
      </div>
    </section>
  );
}

const SUBJ_OF = (code) => (/-ma-|maths|num-/.test(code) ? "Maths" : /-en-|-lit-|eng-|read/.test(code) ? "English" : /-sc-/.test(code) ? "Science" : /-geo-/.test(code) ? "Geography" : /-hi-/.test(code) ? "History" : null);
/** YOUR JOURNEY — one-line-per-subject summary from existing evidence + week + places. No new statistics. */
export function HomeJourney() {
  const p = getLearningProfile();
  const bySubject = {};
  p.currentObjectives.forEach((c) => { const s = SUBJ_OF(c); if (s && !bySubject[s]) bySubject[s] = "growing"; });
  p.completedObjectives.forEach((c) => { const s = SUBJ_OF(c); if (s && !bySubject[s]) bySubject[s] = "secure"; });
  const lines = [
    p.TTQSState.status !== "new" && `Maths — times tables ${p.TTQSState.status === "repair" ? "need a repair" : p.TTQSState.status === "review" ? "ready to review" : "growing"}`,
    p.EnglishState.status !== "not_started" && `English — ${p.EnglishState.status === "practice" ? p.EnglishState.line.toLowerCase() : "growing"}`,
    ...Object.entries(bySubject).filter(([s]) => !(s === "Maths" && p.TTQSState.status !== "new") && !(s === "English" && p.EnglishState.status !== "not_started")).map(([s, st]) => `${s} — ${st === "secure" ? "skills secure" : "skills growing"}`),
    `World — ${p.rewards.places} discover${p.rewards.places === 1 ? "y" : "ies"}`,
  ].filter(Boolean).slice(0, 4);
  return (
    <section className="mt-6 md:mt-8 rounded-3xl border-[3px] border-slate-900 bg-[#fffaf0] p-4" data-testid="home-journey-summary" aria-labelledby="home-journey-title">
      <p className="text-[11px] font-black uppercase tracking-widest text-slate-600">📍 Your journey</p>
      <h2 id="home-journey-title" className="font-heading text-lg md:text-xl font-bold">Week {p.journeyWeek} of {p.journeyTotal}</h2>
      <ul className="mt-2 text-sm font-bold grid sm:grid-cols-2 gap-1" data-testid="home-journey-lines">{lines.map((l) => <li key={l}>{l}</li>)}</ul>
      <div className="mt-3 grid grid-cols-3 gap-2" data-testid="today-myworld">
        {[{ to: "/my-quest", label: "My Quest", e: "🎒", id: "home-see-my-world" }, { to: "/passport", label: `Passport · ⭐ ${p.rewards.stars}`, e: "🗺️", id: "myworld-passport" }, { to: "/portfolio", label: "My creations", e: "🛠️", id: "myworld-my-creations" }].map((l) => (
          <Link key={l.to} to={l.to} data-testid={l.id} className="min-h-[60px] rounded-2xl border-[3px] border-slate-900 bg-white flex flex-col items-center justify-center gap-0.5 font-bold text-xs sm:text-sm shadow-[0_3px_0_0_rgba(15,23,42,1)] hover:bg-yellow-50"><span aria-hidden className="text-xl">{l.e}</span><span className="text-center leading-tight">{l.label}</span></Link>
        ))}
      </div>
    </section>
  );
}
export const countryByName = (name) => COUNTRIES.find((c) => c.name === name) || null;
